import { useQuery, useMutation } from 'convex/react';
import { api } from '../../api';
import { useToast } from '@/hooks/use-toast';

export interface LevelBrowserPolicy {
  _id: string;
  _creationTime: number;
  levelId: string;
  categories: string[];
  allowlist?: string[];
  blocklist?: string[];
  blockVideos?: boolean;
  accuracy?: number;
}

export const useLevelBrowserPolicies = (levelId: string) => {
  const { toast } = useToast();
  
  // Get the actual level data that contains browser policy information
  const levelData = useQuery(api.levels.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100
    }
  });

  const updateLevelBrowserMutation = useMutation(api.levels.policies.update);

  const updateBrowserPolicy = async (data: { categories?: string[]; allowlist?: string[]; blocklist?: string[]; blockVideos?: boolean; accuracy?: number }) => {
    try {
      // Find the level object by Convex ID
      const currentLevel = levelData?.page?.find(level => level._id === levelId);
      if (!currentLevel) {
        throw new Error("Level not found");
      }

      await updateLevelBrowserMutation({
        level: currentLevel._id as any,
        policyCategories: data.categories || [],
        allowlist: data.allowlist,
        blocklist: data.blocklist,
        blueJayRules: (data.blockVideos !== undefined || data.accuracy !== undefined) ? { 
          ...(currentLevel.blueJayRules || {}), 
          ...(data.blockVideos !== undefined && { blockVideos: data.blockVideos }),
          ...(data.accuracy !== undefined && { accuracy: data.accuracy })
        } : currentLevel.blueJayRules
      });
      
      toast({
        title: "Success",
        description: "Browser policy updated successfully",
      });
    } catch (error) {
      console.error('Error updating browser policy:', error);
      toast({
        title: "Error",
        description: "Failed to update browser policy",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Find the specific level from the list
  const currentLevel = levelData?.page?.find(level => level._id === levelId);

  return {
    browserPolicy: currentLevel ? {
      _id: currentLevel._id,
      _creationTime: Date.now(),
      levelId: currentLevel._id,
      categories: currentLevel.policyCategories || [],
      allowlist: currentLevel.allowedUrls || [], // Fixed: use allowedUrls for allowlist
      blocklist: currentLevel.blockedUrls || [], // Fixed: use blockedUrls for blocklist
      blockVideos: currentLevel.blueJayRules?.blockVideos || false,
      accuracy: currentLevel.blueJayRules?.accuracy
    } : null,
    isLoading: levelData === undefined,
    updateBrowserPolicy
  };
};